/**
 * ProposalRevisionView — read-only display of a saved proposal revision.
 *
 * PR 6b — Phase 3 migration: consumes the discriminated `ProposalResponse`
 * union (admin / comercial) instead of the legacy `ProposalRevision` model.
 * Role-aware columns: admin sees Custo unit + Margem; comercial does not.
 * Both roles see Tipo, Produto, Qty, Rev4 unit., Valor ref., Rev3 unit.,
 * Subtotal, and a per-row `margin_at_risk` chip.
 *
 * Subtotal semantics: column reflects the NEGOTIATED price
 * (`unit_price_cents × qty`), NOT the catalog PVP × qty. PVP stays as the
 * "PVP unit." reference column so the discount magnitude is still visible.
 * The footer "Preço final" uses `revision.total_cents` straight from the
 * backend — never recomputed client-side, so admin and comercial agree.
 *
 * Reference badge: F2-VR — renders a Chip with the effective reference type
 * (REV3 / COST / REV4). Legacy revisions (reference_type null/undefined) are
 * treated as COST and tagged "(legacy)".
 *
 * Role narrowing convention (ADR PR 6a): narrow `response.role` BEFORE
 * reading `response.revision`. The discriminator is on response, not on
 * revision.
 */
import React from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Typography,
  Box,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  type ProposalResponse,
  type AdminItemDTO,
  type ComercialItemDTO,
  effectiveReferenceType,
  isLegacyReference,
} from "../../model/Proposal";
import { hasCostCents, hasMarginPct } from "../../helpers/proposalDto";

interface Props {
  response: ProposalResponse;
  showChangeRequestJustification?: boolean;
}

function formatCents(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

/** Format a 0..1 margin pct as a pt-PT percentage. */
const MARGIN_FORMATTER = new Intl.NumberFormat("pt-PT", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: "exceptZero",
});

/** Per-unit reference value in cents, or undefined when no value is available.
 *  Both DTO shapes (admin/comercial) carry rev3_cents; only admin carries
 *  cost_cents. For comercial under COST mode we fall back to rev3_cents —
 *  comercial role cannot see cost. Server-side is the source of truth
 *  (`min_price_cents` is already computed against the correct reference);
 *  this client-side function is presentational only. */
function itemRefUnit(
  item: AdminItemDTO | ComercialItemDTO,
  effective: "REV3" | "COST" | "REV4",
): number | undefined {
  if (effective === "REV3") {
    return item.rev3_cents;
  }
  if (effective === "REV4") {
    // Free reference: REV4 → REV3 → cost (admin only) fallback chain.
    if (item.rev4_cents !== undefined) return item.rev4_cents;
    if (item.rev3_cents !== undefined) return item.rev3_cents;
    // Admin branch: prefer cost when both rev3 and rev4 absent.
    if ("cost_cents" in item) return item.cost_cents;
    return undefined;
  }
  // COST mode. Comercial doesn't carry cost — fall back to rev3.
  if ("cost_cents" in item) return item.cost_cents;
  return item.rev3_cents;
}

const ProposalRevisionView: React.FC<Props> = ({
  response,
  showChangeRequestJustification,
}) => {
  // Narrow on role FIRST. After this branch, response.revision.items is
  // either AdminItemDTO[] or ComercialItemDTO[] — never the union.
  const isAdmin = response.role === "admin";
  const { revision } = response;
  const items = revision.items;

  const effective = effectiveReferenceType(revision.reference_type);
  const legacy = isLegacyReference(revision.reference_type);

  const refColumnLabel =
    effective === "REV3"
      ? "Valor ref. (Rev3)"
      : effective === "REV4"
        ? "Valor ref. (Rev4)"
        : "Valor ref. (Custo)";

  const badgeLabel = legacy
    ? "Ref: Custo (legacy)"
    : effective === "REV3"
      ? "Ref: Rev3"
      : effective === "REV4"
        ? "Ref: Rev4"
        : "Ref: Custo";

  const badgeColor: "default" | "primary" | "warning" | "info" = legacy
    ? "warning"
    : effective === "REV3"
      ? "primary"
      : effective === "REV4"
        ? "info"
        : "default";

  const badgeSx =
    !legacy && effective === "COST"
      ? { bgcolor: "#6B7280", color: "#fff", fontWeight: 600 }
      : { fontWeight: 600 };

  const badgeTooltip = legacy
    ? "Revisão anterior à migração F2-VR — tratada como Custo"
    : effective === "REV3"
      ? "Valor de referência: preço Rev3 (lista revendedor)"
      : effective === "REV4"
        ? "Valor de referência: preço Rev4 (revendedor)"
        : "Valor de referência: custo de catálogo";

  const subtotalUnitCents = (item: AdminItemDTO | ComercialItemDTO) =>
    item.unit_price_cents * item.quantity;

  const totalRefCents = items.reduce<number | undefined>((sum, i) => {
    const ref = itemRefUnit(i, effective);
    if (ref === undefined) return sum;
    return (sum ?? 0) + ref * i.quantity;
  }, undefined);

  const totalRev4Cents = items.some((i) => i.rev4_cents !== undefined)
    ? items.reduce((sum, i) => sum + (i.rev4_cents ?? 0) * i.quantity, 0)
    : undefined;

  const totalRev3Cents = items.reduce(
    (sum, i) => sum + (i.rev3_cents ?? i.pvp_cents ?? 0) * i.quantity,
    0,
  );

  return (
    <Box data-testid="proposal-revision-view" data-role={response.role}>
      {showChangeRequestJustification &&
        revision.change_request_justification && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Pedido de alteração:</strong>{" "}
            {revision.change_request_justification}
          </Alert>
        )}

      {/* F2-VR — reference type badge */}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title={badgeTooltip}>
          <Chip
            label={badgeLabel}
            color={badgeColor}
            size="small"
            sx={badgeSx}
            data-testid="reference-type-badge"
            aria-label={badgeTooltip}
          />
        </Tooltip>
      </Box>

      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
            <TableCell sx={{ width: 140 }}>Tipo</TableCell>
            <TableCell>Produto</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right">Rev4 unit.</TableCell>
            <TableCell align="right">{refColumnLabel} unit.</TableCell>
            <TableCell align="right">PVP/Rev3 unit.</TableCell>
            {isAdmin && (
              <TableCell align="right" data-testid="header-cost">
                Custo unit.
              </TableCell>
            )}
            {isAdmin && (
              <TableCell align="right" data-testid="header-margin">
                Margem
              </TableCell>
            )}
            <TableCell align="right">Subtotal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, idx) => (
            <TableRow
              key={item.product_id + idx}
              data-testid={`row-${item.product_id}`}
            >
              <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                {item.component_type ?? "—"}
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.product_id}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                    >
                      {item.name}
                    </Typography>
                  </Box>
                  {item.margin_at_risk && (
                    <Tooltip title="Preço unitário abaixo do mínimo permitido — margem em risco">
                      <Chip
                        label="Margem em risco"
                        size="small"
                        role="img"
                        tabIndex={0}
                        aria-label="Margem em risco: preço abaixo do mínimo permitido"
                        data-testid={`margin-at-risk-${item.product_id}`}
                        sx={{
                          bgcolor: "#92400E",
                          color: "white",
                          fontWeight: 600,
                          height: 20,
                          fontSize: 11,
                        }}
                      />
                    </Tooltip>
                  )}
                  {item.rev3_cents !== undefined && item.rev3_cents === 0 && (
                    <Tooltip title="Preço Rev3 não definido para este produto">
                      <Chip
                        label="Sem Rev3"
                        size="small"
                        role="img"
                        aria-label="Produto sem preço Rev3 definido no catálogo"
                        data-testid={`no-rev3-${item.product_id}`}
                        sx={{
                          bgcolor: "#D97706",
                          color: "white",
                          fontWeight: 600,
                          height: 20,
                          fontSize: 11,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell align="right">{item.quantity}</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {item.rev4_cents !== undefined
                  ? formatCents(item.rev4_cents)
                  : "—"}
              </TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {(() => {
                  const ref = itemRefUnit(item, effective);
                  return ref !== undefined ? formatCents(ref) : "—";
                })()}
              </TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {item.rev3_cents !== undefined || item.pvp_cents !== undefined
                  ? formatCents(item.rev3_cents ?? item.pvp_cents ?? 0)
                  : "—"}
              </TableCell>
              {isAdmin && hasCostCents(item) && (
                <TableCell
                  align="right"
                  sx={{ color: "text.secondary" }}
                  data-testid={`cell-cost-${item.product_id}`}
                >
                  {formatCents(item.cost_cents)}
                </TableCell>
              )}
              {isAdmin && hasMarginPct(item) && (
                <TableCell
                  align="right"
                  data-testid={`cell-margin-${item.product_id}`}
                  sx={{
                    color: item.margin_pct < 0 ? "#B91C1C" : "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {MARGIN_FORMATTER.format(item.margin_pct)}
                </TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {formatCents(subtotalUnitCents(item))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ "& td": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
            <TableCell colSpan={3}>Total</TableCell>
            <TableCell align="right">
              {totalRev4Cents !== undefined ? formatCents(totalRev4Cents) : "—"}
            </TableCell>
            <TableCell align="right">
              {totalRefCents !== undefined ? formatCents(totalRefCents) : "—"}
            </TableCell>
            <TableCell align="right">{formatCents(totalRev3Cents)}</TableCell>
            {isAdmin && <TableCell />}
            {isAdmin && <TableCell />}
            <TableCell align="right">
              {formatCents(revision.total_cents)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 2,
            px: 3,
            py: 1,
            textAlign: "right",
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.85, display: "block" }}
          >
            Preço Final da Proposta
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatCents(revision.total_cents)}
          </Typography>
        </Box>
      </Box>

      {revision.notes && (
        <Box
          sx={{
            bgcolor: "#fffde7",
            border: "1px solid #f9a825",
            borderRadius: 1,
            p: 1.5,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Notas internas
          </Typography>
          <Typography variant="body2">{revision.notes}</Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Revisão v{revision.version} — guardada por {revision.created_by || "—"}
        {revision.created_at
          ? ` em ${new Date(revision.created_at).toLocaleString("pt-PT")}`
          : ""}
      </Typography>
    </Box>
  );
};

export default ProposalRevisionView;
